import { ActionContext } from "vuex";
import { LoggerService as lgg } from "vue-components-library";

import { CaseService, ICase } from "@/Case/services";
import CasesService, { dateXDaysAgo } from "@/Cases/services/Cases.Service";
import { UtcHelper } from "./Utc.helper";
import LoginStore from "../../Login/store/Login.Store";
import { date5DaysAgo, LOG_TAG } from "@/Cases/store/constants";
import { getInitialTimespan } from "@/Cases/store/functions/getInitialTimespan";

function getDefaultState() {
  const timespan = getInitialTimespan();

  return {
    timespan,
    data30Days: [],
    dataMonthly: [],
    fromDate: UtcHelper.getMonthInitialDate(),
    toDate: UtcHelper.getMonthEndDate(),
    selectedItem: null,
    useSavedDate: false
  };
}

const state = getDefaultState();

const getters = {
  data30Days: (state: any) => state.data30Days,
  dataMonthly: (state: any) => state.dataMonthly,
  fromDate: (state: any) => state.fromDate,
  selectedItem: (state: any) => state.selectedItem,
  totalItems: (state: any) => state.data30Days.length,
  totalItemsMonthly: (state: any) => state.dataMonthly.length,
  totalItemsToday: (state: any) =>
    state.data30Days.filter((item: any) => item.createdAt >= UtcHelper.getToday()).length,
  thisItem: (state: any) => {
    if (!state.selectedItem) {
      return null;
    }
    return state.dataMonthly.filter((item: any) => item.id === state.selectedItem)[0];
  },
  numberOfDaysToShowCases: (state: { timespan: string }): number => {
    if (LoginStore.getters.isAdmin(LoginStore.state)) {
      if (state.timespan === "month") {
        return 30;
      }

      return 6;
    }

    if (state.timespan === "month") {
      return 6;
    }

    return 4;
  },
  casesPerDayReportDates(state) {
    let reportDates: Array<String> = [];
    for (let i = getters.numberOfDaysToShowCases(state); i >= 0; i--) {
      let reportDate = dateXDaysAgo(i);
      if (UtcHelper.isEasternUtcOffset()) {
        reportDate = UtcHelper.setEasternReportDate(reportDate);
      }
      reportDates.push(reportDate);
    }
    return reportDates;
  },
  casesPerDayReportValues: (state: any) => {
    let reportValues: Array<number> = [];
    for (let i = getters.numberOfDaysToShowCases(state); i >= 0; i--) {
      const reportDate = UtcHelper.formatInitialDate({ date: dateXDaysAgo(i) });
      const reportDateNextDay = UtcHelper.getNextDay({ date: reportDate });
      reportValues.push(
        state.data30Days.filter((item: any) => {
          item.createdAt >= reportDate && item.createdAt <= reportDateNextDay;
        }).length
      );
    }
    return reportValues;
  },
  casesPerDayReportValuesPriority0: (state: any) => {
    return getters.casesPerDayReportValuesPriority(state, 0);
  },
  casesPerDayReportValuesPriority1: (state: any) => {
    return getters.casesPerDayReportValuesPriority(state, 1);
  },
  casesPerDayReportValuesPriority2: (state: any) => {
    return getters.casesPerDayReportValuesPriority(state, 2);
  },
  casesPerDayReportValuesPriority3: (state: any) => {
    return getters.casesPerDayReportValuesPriority(state, 3);
  },

  casesPerDayReportValuesPriority: (state: any, priority: number) => {
    let reportValues: Array<number> = [];

    for (let i = getters.numberOfDaysToShowCases(state); i >= 0; i--) {
      const reportDate = UtcHelper.formatInitialDate({ date: dateXDaysAgo(i) });
      const reportDateEndOfDay = UtcHelper.getEndOfDay({ date: reportDate });

      const dateCases = state.data30Days.filter(
        (item: any) =>
          item.createdAt >= reportDate &&
          item.createdAt < reportDateEndOfDay &&
          (item.complaint ? item.complaint.priority : 0) === priority
      );

      reportValues.push(dateCases.length);
    }

    return reportValues;
  }
};

export const actions = {
  selectItem(context: ActionContext<any, any>, id: String) {
    context.commit("SELECT_ITEM", id);
  },
  async loadData(context: ActionContext<any, any>, emsAgencyId: String) {
    try {
      context.dispatch("Global/loading", null, { root: true });
      const twiageCases = await CasesService.getCases({
        numberOfDaysToGetCases: getters.numberOfDaysToShowCases(context.state),
        emsAgencyId
      });
      context.commit("SET_DATA", twiageCases);
      context.dispatch("Global/doneLoading", null, { root: true });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to load Cases`, { error });
    }
  },
  async loadMonthlyData(context: ActionContext<any, any>, emsAgencyId: String) {
    const isAdmin = LoginStore.getters.isAdmin(LoginStore.state);

    try {
      context.dispatch("Global/loading", null, { root: true });

      const casesParams = {
        emsAgencyId,
        fromDate: isAdmin
          ? state.fromDate
          : UtcHelper.formatInitialDate({
              date: date5DaysAgo
            }),
        toDate: isAdmin ? state.toDate : undefined
      };

      const twiageCases = await CasesService.getCases(casesParams);

      context.commit("SET_DATA_MONTHLY", twiageCases);
      context.commit("SET_USE_SAVED_DATE", isAdmin);
      context.dispatch("augmentData");
      context.dispatch("Global/doneLoading", null, { root: true });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to load monthly Cases`, { error });
    }
  },
  augmentData(context: ActionContext<any, any>) {
    context.dispatch("Global/loading", null, { root: true });
    context.commit("AUGMENT_DATA");
    context.dispatch("Global/doneLoading", null, { root: true });
  },
  setFilterDates(context: ActionContext<any, any>, data: any) {
    if (UtcHelper.isEasternUtcOffset()) {
      data.fromDate = UtcHelper.getEasternUtcFilterInitialDate({ date: data.fromDate });
    }

    if (UtcHelper.isWesternUtcOffset()) {
      data.toDate = UtcHelper.getWesternUtcFilterEndDate({ date: data.toDate });
    }

    context.commit("SET_FILTER_DATES", data);
    context.dispatch("loadMonthlyData", data.emsAgencyId);
  },
  refresh(context: ActionContext<any, any>, emsAgencyId: String) {
    context.dispatch("loadMonthlyData", emsAgencyId);
  }
};

const mutations = {
  SET_FROM_DATE(state: any, fromDate: String) {
    state.fromDate = fromDate;
  },
  SET_TO_DATE(state: any, toDate: String) {
    state.toDate = toDate;
  },
  SET_DATA(state: any, data: Array<ICase>) {
    state.data30Days = data.map((item: ICase) => CaseService.augmentData(item));
  },
  SET_DATA_MONTHLY(state: any, data: Array<ICase>) {
    state.dataMonthly = data.map((item: ICase) => CaseService.augmentData(item));
  },
  AUGMENT_DATA(state: any): void {
    state.dataMonthly = state.dataMonthly.map(item => {
      const patientInfo = {};
      if (item.patientInfo) {
        patientInfo["data"] = "";
        item.patientInfo.map(row => {
          patientInfo[row.key] = row.value;
          patientInfo["data"] = patientInfo["data"] + row.value + " ";
        });
      }
      return Object.assign(
        {
          pti: patientInfo
        },
        item
      );
    });
  },
  SET_FILTER_DATES(state: any, data: any) {
    state.fromDate = data.fromDate;
    state.toDate = data.toDate;
  },
  SELECT_ITEM(state: any, id: String) {
    state.selectedItem = state.selectedItem === id ? null : id;
  },
  SET_USE_SAVED_DATE(state: any, useSavedDate: boolean) {
    state.useSavedDate = useSavedDate;
  }
};

const store: any = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};

export default store;
